/**
 * Vyhledávací lišta (verze 2) — Příjezd · Odjezd · Hosté · Typ pobytu → „Zobrazit dostupnost".
 *
 * Host přichází s termínem, ne s typem ubytování. Lišta se zeptá na termín a
 * pak ukáže, co je v něm opravdu volné a za kolik (skutečná cena za celý
 * pobyt z /quote, ne „od"). „Rezervovat" otevře náš rezervační modal už
 * s předvyplněným termínem a počtem hostů — zbývá vybrat jednotku a jméno.
 *
 * Dvě skupiny (Robin 5. 11. 2026): Ubytování = pokoj penzionu,
 * Rybolov = rybářská chatka, Tiny House, bivak, karavan. Tlačítka v hero posílají událost
 * `sobacov:search-group`, která lištu přepne na svou skupinu.
 *
 * Žádná nová cenová logika: dostupnost = /availability/summary, cena = /quote,
 * tedy stejné funkce, kterými se pak rezervace účtuje.
 */
import { useEffect, useMemo, useState } from 'react'
import { apiUrl } from '../lib/api'
import { BOOKING_TYPES, openBooking, penzionUrlFrom } from '../lib/booking'
import { useWebContent } from '../lib/webPrices'

type LodgingKey = 'penzion' | 'chatka' | 'glamping_tiny' | 'kemp' | 'karavan'
type Group = 'ubytovani' | 'rybolov'
type TypeSel = 'all' | `g:${Group}` | LodgingKey

const GROUPS: { key: Group; label: string }[] = [
  { key: 'ubytovani', label: 'Ubytování' },
  { key: 'rybolov', label: 'Rybolov' },
]

const LODGING: { key: LodgingKey; group: Group; resType: string; label: string; image: string; capacity: number; note: string }[] = [
  { key: 'penzion',       group: 'ubytovani', resType: 'penzion',   label: 'Pokoj penzionu',  image: '/img/penzion_01_zakouti_kreslo.jpg', capacity: 4, note: 'vlastní koupelna · snídaně možná' },
  { key: 'chatka',        group: 'rybolov',   resType: 'chatka',    label: 'Rybářská chatka', image: '/img/chatka_01_chatka_sunset.jpg',   capacity: 4, note: 'přímo u vody · lovné místo' },
  { key: 'glamping_tiny', group: 'rybolov',   resType: 'tinyhouse', label: 'Tiny House',      image: '/img/tinyhouse_01_sauna_hottub.jpg', capacity: 4, note: 'sauna · min. 2 noci' },
  { key: 'kemp',          group: 'rybolov',   resType: 'kemp',      label: 'Bivak',           image: '/img/bivak_01_zapad.webp',           capacity: 4, note: '12h sloty · vlastní vybavení' },
  { key: 'karavan',       group: 'rybolov',   resType: 'karavan',   label: 'Karavan',         image: '/img/karavan_01_zapad.webp',         capacity: 4, note: 'stání · el. přípojka' },
]

type Row = {
  key: LodgingKey
  available: number
  total: number
  price: number | null
  nights: number
  error?: string
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const czDate = (s: string) => { const [y, m, d] = s.split('-').map(Number); return `${d}. ${m}. ${y}` }
const czk = (n: number) => n.toLocaleString('cs-CZ')

export function SearchBar() {
  const content = useWebContent()
  const penzionUrl = penzionUrlFrom(content as Record<string, unknown>)   // penzion dočasně jen přes Booking.com (Robin 26. 10.)
  const today = useMemo(() => iso(new Date()), [])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [guests, setGuests] = useState(2)
  const [type, setType] = useState<TypeSel>('all')
  const [rows, setRows] = useState<Row[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Odjezd nesmí být před příjezdem — při změně příjezdu posuň odjezd
  useEffect(() => {
    if (from && (!to || to <= from)) {
      const d = new Date(from + 'T12:00:00'); d.setDate(d.getDate() + 1); setTo(iso(d))
    }
  }, [from]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tlačítka v hero: „Rezervovat ubytování" / „Rezervovat rybolov" → přepnout skupinu
  useEffect(() => {
    const on = (e: Event) => {
      const g = (e as CustomEvent<Group>).detail
      if (g === 'ubytovani' || g === 'rybolov') { setType(`g:${g}`); setRows(null); setErr(null) }
    }
    document.addEventListener('sobacov:search-group', on)
    return () => document.removeEventListener('sobacov:search-group', on)
  }, [])

  const nights = from && to ? Math.max(0, Math.round((new Date(to + 'T12:00:00').getTime() - new Date(from + 'T12:00:00').getTime()) / 86400000)) : 0

  function matches(l: (typeof LODGING)[number]): boolean {
    if (type === 'all') return true
    if (type.startsWith('g:')) return l.group === type.slice(2)
    return l.key === type
  }

  async function search() {
    if (!from || !to || nights < 1) { setErr('Vyberte datum příjezdu a odjezdu.'); return }
    setBusy(true); setErr(null); setRows(null)
    try {
      const sum = await fetch(apiUrl(`/api/sobacov/availability/summary?from_dt=${from}&to_dt=${to}`)).then((r) => r.json())
      const byType: Record<string, { total: number; available: number }> = {}
      for (const t of sum.by_type || []) byType[t.type] = { total: t.total, available: t.available }
      const wanted = LODGING.filter(matches)
      const out: Row[] = await Promise.all(wanted.map(async (l) => {
        if (l.key === 'penzion' && penzionUrl) return { key: l.key, available: 0, total: 0, price: null, nights, error: 'rezervace přes Booking.com' }
        const av = byType[l.resType] || { total: 0, available: 0 }
        let price: number | null = null
        let error: string | undefined
        try {
          const body: Record<string, unknown> = { reservation_type: l.resType, num_persons: guests }
          if (l.resType === 'kemp') { body.from_dt = `${from}T07:00`; body.to_dt = `${to}T07:00` }
          else { body.from_dt = from; body.to_dt = to }
          const q = await fetch(apiUrl('/api/sobacov/quote'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
          const j = await q.json()
          if (q.ok && j.ok) price = j.price_czk
          else error = j.detail || 'cena není k dispozici'
        } catch { error = 'cena není k dispozici' }
        return { key: l.key, available: av.available, total: av.total, price, nights, error }
      }))
      setRows(out)
    } catch {
      setErr('Dostupnost se nepodařilo načíst. Zkuste to prosím znovu nebo zavolejte 775 140 705.')
    } finally { setBusy(false) }
  }

  function book(key: LodgingKey) {
    const d = BOOKING_TYPES[key]
    openBooking({ ...d, from_dt: from, to_dt: to, persons: guests })
  }

  const field = 'flex-1 min-w-0 px-4 py-3 md:py-4 flex items-center gap-3'
  const lbl = 'block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-700/70'
  const inp = 'block w-full bg-transparent text-[15px] font-semibold text-ink-900 focus:outline-none appearance-none pr-6'
  const showHeadings = type === 'all'

  return (
    <section id="rezervace" className="relative z-20 -mt-10 md:-mt-12 mb-10">
      <div className="container-x">
        <div className="bg-white rounded-2xl shadow-[0_24px_70px_-24px_rgba(13,26,19,0.45)] ring-1 ring-ink-900/[0.07]">
          <form
            onSubmit={(e) => { e.preventDefault(); search() }}
            className="flex flex-col md:flex-row md:items-stretch divide-y md:divide-y-0 md:divide-x divide-ink-900/[0.08]"
          >
            <label className={field}>
              <CalIcon />
              <span className="min-w-0 flex-1">
                <span className={lbl}>Příjezd</span>
                <input type="date" min={today} value={from} onChange={(e) => setFrom(e.target.value)} className={inp} required />
              </span>
            </label>
            <label className={field}>
              <CalIcon />
              <span className="min-w-0 flex-1">
                <span className={lbl}>Odjezd</span>
                <input type="date" min={from || today} value={to} onChange={(e) => setTo(e.target.value)} className={inp} required />
              </span>
            </label>
            <label className={field}>
              <GuestIcon />
              <span className="min-w-0 flex-1">
                <span className={lbl}>Hosté</span>
                <select value={guests} onChange={(e) => setGuests(parseInt(e.target.value, 10))} className={inp}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} {n === 1 ? 'host' : n < 5 ? 'hosté' : 'hostů'}</option>)}
                </select>
              </span>
              <Chevron />
            </label>
            <label className={field}>
              <BedIcon />
              <span className="min-w-0 flex-1">
                <span className={lbl}>Ubytování · rybolov</span>
                <select value={type} onChange={(e) => setType(e.target.value as TypeSel)} className={inp}>
                  <option value="all">Všechny možnosti</option>
                  {GROUPS.map((g) => (
                    <optgroup key={g.key} label={g.label}>
                      <option value={`g:${g.key}`}>Vše · {g.label.toLowerCase()}</option>
                      {LODGING.filter((l) => l.group === g.key).map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                    </optgroup>
                  ))}
                </select>
              </span>
              <Chevron />
            </label>
            <div className="p-3 md:p-2.5 flex items-center">
              <button type="submit" disabled={busy} className="btn-book w-full md:w-auto whitespace-nowrap disabled:opacity-60">
                {busy ? 'Hledám…' : 'Zobrazit dostupnost →'}
              </button>
            </div>
          </form>

          {err && <div className="px-5 py-3 text-brand-600 text-sm font-semibold border-t border-ink-900/[0.08]">{err}</div>}

          {rows && (
            <div className="border-t border-ink-900/[0.08]">
              <div className="px-5 pt-4 pb-2 flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-700/70">
                  {czDate(from)} – {czDate(to)} · {nights} {nights === 1 ? 'noc' : nights < 5 ? 'noci' : 'nocí'} · {guests} {guests === 1 ? 'host' : guests < 5 ? 'hosté' : 'hostů'}
                </div>
                <div className="text-[12px] text-ink-700/70">ceny jsou za celý pobyt, bez příplatků</div>
              </div>
              {GROUPS.map((g) => {
                const list = rows.filter((r) => LODGING.find((x) => x.key === r.key)!.group === g.key)
                if (!list.length) return null
                return (
                  <div key={g.key}>
                    {showHeadings && (
                      <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] font-bold text-ink-700/60 border-t border-ink-900/[0.05]">
                        {g.label}
                      </div>
                    )}
                    <ul className="divide-y divide-ink-900/[0.06]">
                      {list.map((r) => {
                        const l = LODGING.find((x) => x.key === r.key)!
                        const tooMany = guests > l.capacity
                        const free = r.available > 0 && !tooMany
                        const ext = l.key === 'penzion' ? penzionUrl : null   // pokoje zatím jen přes Booking.com
                        return (
                          <li key={r.key} className="px-5 py-3 flex items-center gap-4">
                            <img src={l.image} alt="" className="w-20 h-14 object-cover rounded-lg shrink-0 hidden sm:block" loading="lazy" />
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-ink-900 text-[15px] leading-tight">{l.label}</div>
                              <div className="text-[12px] text-ink-700/80 mt-0.5">
                                {ext ? 'pokoje penzionu zatím rezervujete na Booking.com' : ((content[`search_note_${l.key}`] as string) || l.note)}
                                {!ext && tooMany && <span className="text-brand-600 font-semibold"> · pro {guests} hostů vyberte víc jednotek</span>}
                              </div>
                            </div>
                            {ext ? (
                              <a href={ext} target="_blank" rel="noopener" className="btn-book text-[13px] py-2 px-4 shrink-0 whitespace-nowrap">
                                Booking.com ↗
                              </a>
                            ) : (
                              <>
                                <div className="text-right shrink-0">
                                  <div className={`text-[12px] font-bold ${free ? (r.available <= 2 ? 'text-amber-700' : 'text-forest-700') : 'text-ink-700/50'}`}>
                                    {r.available > 0 ? `${r.available} ${r.available === 1 ? 'volná' : r.available < 5 ? 'volné' : 'volných'} z ${r.total}` : 'obsazeno'}
                                  </div>
                                  <div className="text-[15px] font-bold text-ink-900 tabular-nums">
                                    {r.price != null ? `${czk(r.price)} Kč` : (r.error || '—')}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={!free}
                                  onClick={() => book(r.key)}
                                  className="btn-book text-[13px] py-2 px-4 shrink-0 disabled:opacity-35 disabled:cursor-not-allowed"
                                >
                                  Rezervovat
                                </button>
                              </>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function Chevron() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 text-ink-700/60 shrink-0 -ml-5 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}
function CalIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 text-ink-800 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  )
}
function GuestIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 text-ink-800 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}
function BedIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 text-ink-800 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18V8M3 18h18v-6a3 3 0 0 0-3-3h-7a3 3 0 0 0-3 3v3H3M21 18v2M3 18v2" />
    </svg>
  )
}
